import CustomBreadcrumbs from '@/commons/custom-breadcrumbs';
import { Iconic } from '@/commons/iconic';
import Label from '@/commons/label/Label';
import Logo from '@/commons/logo';
import Splash from '@/commons/splash';
import { TableInvoice } from '@/commons/table-invoice';
import { useRole } from '@/hooks/useRole';
import { STATUS } from '@/modules/reservations/utils/constants';
import { PATH_DASHBOARD } from '@/routes/paths';
import { changeOrderService, getPrintCommandersService, postFactureService } from '@/services';
import { Roles, axios, formatDateWithTime, parseUserDate } from '@/utils';
import { currentFormat } from '@/utils/cases';
import { errorOrders } from '@/utils/errors';
import { translates } from '@/utils/translate';
import { Box, Card, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  ReactPortal,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useCashRegisterContext } from '@/hooks/useCashRegisterContext';
import { TITLE, commonTableDateFormat } from '@/utils/constants';
import { useCompanyCategory } from "@hooks/useCompanyCategory";
import { Button } from "@PorkyCol/button";
import { useModal } from '@/hooks/useModal';
import { useFetch } from '../hooks/useFetch';
import { OrderDetail } from '../types';
import { COLORS } from '../utils/constants';
import { DiscountForm } from '../form/discount';

export default function OrderViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {isFoodCompany} = useCompanyCategory();
  const [data, setData] = useState<any>({});
  const [isLoading, setLoading] = useState<any>(true);
  const { enqueueSnackbar } = useSnackbar();
  const { authorized: authorizedWithAtm } = useRole([Roles.ADMIN, Roles.ATM]);
  const { isOpen, box } = useCashRegisterContext();
  const { open, handleChange } = useModal();

  const sortByLineAndNameProduct = (a: OrderDetail, b: OrderDetail) => {
    const comparatorLine = a.product?.product?.lines[0].name.localeCompare(b.product?.product?.lines[0].name);
    if (comparatorLine === 0) {
      return a.product?.product?.name?.localeCompare(b.product.product.name);
    }

    return comparatorLine;
  };

  const {
    isLoading: isLoadingEdit,
    fetchProperties,
    data: dataEdit,
  } = useFetch({ fetchFunction: changeOrderService, getData: true });

  const handleChangeOrderService = async (values: any) => {
    try {
      await fetchProperties(id!,values);
      handleChange();
      await handleOrder();
    } catch (error) { 
      const errorMessage = errorOrders[error?.error] ?? 'Error';
      enqueueSnackbar(errorMessage.label, { variant: 'error' });
    }
  };

  const handleOrder = async () => {
    setLoading(true);
    try {
      const dataTemp = await axios.get(`/orders/${id}`).then((res) => res.data.data);
      if (dataTemp) {
        const paymentMethods: any = [];
        if (dataTemp.facture) {
          const {
            facture: { cash, creditCard, transference },
          } = dataTemp;
          const newMethods: any = {
            cash,
            creditCard,
            transference,
          };
          Object.keys(newMethods).forEach((itemKey) => {
            paymentMethods.push({
              name: translates[itemKey] ?? '',
              value: newMethods[itemKey],
            });
          });
        }
        setData({ ...dataTemp, paymentMethods });
      } else {
        setData({});
      }
    } catch (error) {
      setData({});
    } finally {
      setLoading(false);
    }
  };

  const mutate = async () => {
    try {
      await postFactureService(id!);
      navigate(PATH_DASHBOARD.orders.checkout(id!));
    } catch (error) {
      console.error('🚀 ~ file: view.tsx:93 ~ mutate ~ error:', error);
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const getPrintCommander = async (): Promise<void> => {
    if (id) await getPrintCommandersService(id);
  };

  const generateBodyy = () =>
    data?.ordersDetails?.sort(sortByLineAndNameProduct).map((item: any) => ({
      id: item.id.slice(item.id.length / 1.4),
      fullId: item.id,
      name: item.product?.product?.name,
      quantity: item.quantity,
      description: item.description,
      price: currentFormat(item.priceUnit),
      quantity_discount: item.quantity_discount,
      percentageDiscount: item.discount,
      discount: currentFormat(item.discount_value),
      total: currentFormat(item.total),
      createdAt: formatDateWithTime(item.createdAt),
    })) || [];

  const generateBody = useCallback(() => {
    const newBody = data?.ordersDetails?.sort(sortByLineAndNameProduct).map((item: any) => ({
      id: item.id.slice(item.id.length / 1.4),
      fullId: item.id,
      name: item.product?.product?.name,
      quantity: item.quantity,
      description: item.description,
      price: currentFormat(item.priceUnit),
      quantity_discount: item.quantity_discount,
      percentageDiscount: item.discount,
      discount: currentFormat(item.discount_value),
      total: currentFormat(item.total),
      status: (
        <Chip
          label={translates[item.status] ?? 'item.status'}
          variant="filled"
          color={COLORS[item.status as keyof typeof COLORS] as any}
        />
      ),
    }));
    return newBody || [];
  }, [data]);
  const generateInvoice = useCallback(
    () => ({
      discount: currentFormat(data?.discountOrderValue),
      total: currentFormat(data?.total),
    }),
    [data]
  );
  const orderParams = useCallback(
    () => ({
      fullId: data?.id ?? '',
      id: data?.id?.slice(0, 7) ?? '',
      table: data?.table ?? '',
      status: data?.status ?? 'created',
    }),
    [data]
  );
  const params = orderParams() || { id: '', fullId: '' };
  const body = generateBody();

  useEffect(() => {
    handleOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <Splash />;

  const VALID_ORDER =
    box?.id !== data?.cashRegister?.id ||
    !isOpen ||
    (data?.status !== STATUS.CREATED && data?.status !== STATUS.REOPEN);
  return (
    <>
      <Helmet>
        <title>
          {' '}
          {TITLE}: Order {id ?? ''}
        </title>
      </Helmet>

      <Container maxWidth={false} sx={{ pb: 2 }}>
        <CustomBreadcrumbs
          heading={`${translates.orders} ${params.id ?? ''}`}
          links={[
            { name: translates.dashboard, href: PATH_DASHBOARD.root },
            {
              name: translates.order,
              href: PATH_DASHBOARD.orders.history,
            },
            { name: data?.table ?? 'No table' },
          ]}
          action={[
            !authorizedWithAtm ? (
              <RouterLink to={PATH_DASHBOARD.orders.add(params.fullId)}>
                <Button
                  variant="solid"
                  color="primary"
                  size="sm"
                  disabled={VALID_ORDER}
                  icon={<Iconic icon="eva:plus-fill" />}
                >
                  {translates.add_product}
                </Button>
              </RouterLink>
            ) : (
              <>
                {isFoodCompany ?
                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    disabled={VALID_ORDER}
                    icon={<Iconic icon="material-symbols:print" />}
                    onClick={() => getPrintCommander()}
                  >
                    {translates.print_commander}
                  </Button> : null
                }
                <RouterLink to={PATH_DASHBOARD.orders.edit(params.fullId)}>
                  <Button
                    variant="outline"
                    color="warning"
                    size="sm"
                    disabled={VALID_ORDER}
                    icon={<Iconic icon="eva:edit-outline" />}
                  >
                    {translates.edit_order}
                  </Button>
                </RouterLink>
                <RouterLink to={PATH_DASHBOARD.orders.add(params.fullId)}>
                  <Button
                    variant="outline"
                    color="success"
                    size="sm"
                    disabled={VALID_ORDER}
                    icon={<Iconic icon="eva:plus-fill" />}
                  >
                    {translates.add_product}
                  </Button>
                </RouterLink>
                <RouterLink to={PATH_DASHBOARD.orders.divider(params.fullId)}>
                  <Button
                    variant="solid"
                    color="warning"
                    disabled={VALID_ORDER}
                    size="sm"
                    icon={<Iconic icon="eva:plus-fill" />}
                  >
                    {translates.divider_order}
                  </Button>
                </RouterLink>

                <Button
                  variant="solid"
                  onClick={() => mutate()}
                  color="success"
                  disabled={VALID_ORDER}
                  size="sm"
                  icon={<Iconic icon="mdi:credit-card-settings-outline" />}
                >
                  {translates.pay}
                </Button>

                <Button
                  variant="outline"
                  color="primary"
                  disabled={VALID_ORDER}
                  icon={<Iconic icon="eva:plus-fill" />}
                  onClick={handleChange}
                  size="sm"
                >
                  {translates.add_discount}
                </Button>
              </>
            ),
          ]}
        />

        {isLoading && !body.length ? (
          'Loading'
        ) : (
          <Card sx={{ pt: 5, px: 5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                <Logo disabledLink width={100} height={100} />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                <Box sx={{ textAlign: { sm: 'right' } }}>
                  <Label
                    variant="soft"
                    color={
                      (params?.status === 'done' && 'success') ||
                      (params?.status === 'created' && 'done') ||
                      (params?.status === 'error' && 'error') ||
                      'default'
                    }
                    sx={{ textTransform: 'uppercase', mb: 1 }}
                  >
                    {translates[params?.status]}
                  </Label>

                  <Typography variant="h6">{`${isFoodCompany? translates.waiter : "Vendedor"} : ${data?.vendor?.name}`}</Typography>
                  <Typography variant="h6">{`WKS-${params.id}`}</Typography>
                  {!!data?.paymentMethods?.length && (
                    <Stack direction="row" flexWrap="wrap" spacing={2} justifyContent="flex-end">
                      {data.paymentMethods.map(
                        (
                          item: {
                            name:
                              | string
                              | number
                              | boolean
                              | ReactElement<any, string | JSXElementConstructor<any>>
                              | Iterable<ReactNode>
                              | ReactPortal
                              | null
                              | undefined;
                            value: any;
                          },
                          index: number
                        ) => (
                          <Label
                            variant="soft"
                            key={`${item.name}-${item.value}-${index + 1}`}
                            color="secondary"
                            sx={{ textTransform: 'uppercase', mb: 1 }}
                          >
                            {item.name}: {currentFormat(item.value ?? 0)}
                          </Label>
                        )
                      )}
                    </Stack>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <h1>{translates.order}</h1>
              </Grid>
            </Grid>

            <TableInvoice
              /*  onEdit={params?.status !== 'closed' && onEdit} */
              body={generateBodyy()}
              invoices={generateInvoice()}
              isLoading={isLoadingEdit}
            />
          </Card>
        )}
      </Container>
      <DiscountForm
        isLoading={isLoadingEdit}
        open={open}
        onClose={handleChange}
        onSubmit={handleChangeOrderService}
        initialData={data?.ordersDetails?.sort(sortByLineAndNameProduct)}
      />
    </>
  );
}
